import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiCalendar,
  FiChevronDown,
  FiDownload,
  FiPrinter,
  FiRefreshCw,
  FiSettings,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import { getFinancialAnalysis, getFinancialOverview } from 'services/dashboard.service';
import useDashboardReport from 'hooks/useDashboardReport';
import './financialDashboard.scss';

const DEFAULT_RANGE = () => ({
  date_from: moment().startOf('year').format('YYYY-MM-DD'),
  date_to: moment().format('YYYY-MM-DD'),
});

const EMPTY_DATA = {
  summary: {},
  trend: [],
  top_products: [],
  expense_categories: [],
  debt_aging: [],
  cash_flow: [],
};

const REPORT_TABS = [
  { key: 'overview', label: 'Tổng quan tài chính', path: '/dashboard/financial-overview' },
  { key: 'analysis', label: 'Phân tích tài chính', path: '/dashboard/financial-analysis' },
  { key: 'payables', label: 'Mua hàng & phải trả', path: '/dashboard/payables' },
  { key: 'sales', label: 'Bán hàng & phải thu', path: '/dashboard/sales-analysis' },
  { key: 'inventory', label: 'Tồn kho', path: '/dashboard/inventory-analysis' },
];

const PRODUCT_COLORS = ['#e11d2e', '#149777', '#e9a23b', '#3b82c4', '#8b95a5'];

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value, maximumFractionDigits = 0) =>
  numberValue(value).toLocaleString('vi-VN', { maximumFractionDigits });

const formatMoney = (value) => formatNumber(Math.abs(numberValue(value)));
const formatMillion = (value, maximumFractionDigits = 0) =>
  formatNumber(Math.abs(numberValue(value)) / 1000000, maximumFractionDigits);
const formatSignedMoney = (value) => numberValue(value) < 0 ? `(${formatMoney(value)})` : formatMoney(value);
const formatSignedMillion = (value, maximumFractionDigits = 0) =>
  numberValue(value) < 0 ? `(${formatMillion(value, maximumFractionDigits)})` : formatMillion(value, maximumFractionDigits);
const formatPercent = (value) => `${formatNumber(numberValue(value), 2)}%`;
const hasMetric = (value) => value !== undefined && value !== null && Number.isFinite(Number(value));
const formatOptionalPercent = (value) => hasMetric(value) ? formatPercent(value) : '—';
const formatRatio = (value) => hasMetric(value) ? `${formatNumber(value, 2)} lần` : '—';
const formatDays = (value) => hasMetric(value) ? `${formatNumber(value, 1)} ngày` : '—';

const getRowLabel = (row, index) =>
  row.period_label || (row.month_no ? `T${row.month_no}` : `Kỳ ${index + 1}`);

const PanelHeader = ({ title, unit, dateLabel, compact = false }) => (
  <div className='bc_fd_panel_header'>
    <h3>
      {title} {unit ? <em>({unit})</em> : null}
    </h3>
    <div className='bc_fd_panel_tools'>
      {dateLabel ? <span className='bc_fd_panel_period'>{dateLabel}</span> : null}
      {!compact ? <button type='button' title='Thiết lập hiển thị'><FiSettings /></button> : null}
    </div>
  </div>
);

const EmptyState = ({ loading, message = 'Không có dữ liệu trong kỳ.' }) => (
  <div className={`bc_fd_empty${loading ? ' is-loading' : ''}`}>
    <span />
    <p>{loading ? 'Đang tải dữ liệu...' : message}</p>
  </div>
);

const StatusCard = ({ title, total, current, overdue, unclassified, dueDataAvailable = true, loading, dateLabel }) => {
  const safeTotal = Math.max(0, numberValue(total));
  const safeCurrent = Math.max(0, numberValue(current));
  const safeOverdue = Math.max(0, numberValue(overdue));
  const safeUnclassified = Math.max(0, numberValue(unclassified));
  const currentPercent = safeTotal ? Math.min(100, (safeCurrent / safeTotal) * 100) : 0;

  return (
    <section className='bc_fd_panel bc_fd_status_card'>
      <PanelHeader title={title} unit='Triệu' dateLabel={dateLabel} />
      <strong className='bc_fd_status_total'>{loading ? '...' : formatMillion(safeTotal)}</strong>
      <span className='bc_fd_metric_label'>TỔNG</span>
      {dueDataAvailable ? (
        <>
          <div className='bc_fd_status_split'>
            <div><strong>{loading ? '...' : formatMillion(safeCurrent)}</strong><span>TRONG HẠN</span></div>
            <div><strong>{loading ? '...' : formatMillion(safeOverdue)}</strong><span>QUÁ HẠN</span></div>
          </div>
          <div className='bc_fd_progress' aria-label={`${formatNumber(currentPercent, 1)}% trong hạn`}>
            <i style={{ width: `${currentPercent}%` }} />
          </div>
          {safeUnclassified > 0 ? <span className='bc_fd_metric_label'>{formatMillion(safeUnclassified)} TRIỆU CHƯA KHAI BÁO HẠN NỢ</span> : null}
        </>
      ) : (
        <div className='bc_fd_status_split'>
          <div><strong>{loading ? '...' : formatMillion(safeTotal)}</strong><span>CHƯA PHÂN LOẠI HẠN NỢ</span></div>
          <div><strong>—</strong><span>CHƯA ĐỦ NGÀY ĐẾN HẠN</span></div>
        </div>
      )}
    </section>
  );
};

const FinancialSnapshot = ({ summary, loading, periodLabel }) => {
  const totalCash = numberValue(summary.cash_balance) + numberValue(summary.bank_balance);
  const profit = hasMetric(summary.profit) ? numberValue(summary.profit) : numberValue(summary.revenue) - numberValue(summary.expense);
  const leftRows = [
    ['Tổng tiền', totalCash, true],
    ['Tiền mặt', summary.cash_balance],
    ['Tiền gửi', summary.bank_balance],
    ['Phải thu', summary.receivable, true],
    ['Phải trả', summary.payable, true],
  ];
  const rightRows = [
    ['Doanh thu thuần', summary.revenue, true],
    ['Chi phí', summary.expense, true],
    ['Lợi nhuận', profit, true],
    ['Hàng tồn kho', summary.inventory, true],
  ];

  const renderRows = (rows) => rows.map(([label, value, strong]) => (
    <div className={strong ? 'is-strong' : ''} key={label}>
      <span>{label}</span><b>{loading ? '...' : label === 'Lợi nhuận' ? formatSignedMoney(value) : formatMoney(value)}</b>
    </div>
  ));

  return (
    <section className='bc_fd_panel bc_fd_snapshot'>
      <PanelHeader title='Tình hình tài chính' dateLabel={periodLabel} />
      <div className='bc_fd_snapshot_grid'>
        <div>{renderRows(leftRows)}</div>
        <div>{renderRows(rightRows)}</div>
      </div>
    </section>
  );
};

const MixedBarChart = ({ rows = [], firstKey, secondKey, lineKey, labels, loading }) => {
  if (!rows.length) return <EmptyState loading={loading} />;

  const values = rows.flatMap((row) => [row[firstKey], row[secondKey], row[lineKey]]).map((value) => Math.abs(numberValue(value)));
  const max = Math.max(1, ...values);
  const chartTop = 18;
  const chartBottom = 174;
  const chartHeight = chartBottom - chartTop;
  const groupWidth = 100 / rows.length;
  const points = rows.map((row, index) => {
    const x = groupWidth * index + groupWidth / 2;
    const y = chartBottom - (Math.abs(numberValue(row[lineKey])) / max) * chartHeight;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className='bc_fd_mixed_chart'>
      <div className='bc_fd_chart_plot'>
        {[0, 1, 2, 3].map((line) => <i className='bc_fd_grid_line' style={{ top: `${18 + line * 42}px` }} key={line} />)}
        <svg viewBox='0 0 100 192' preserveAspectRatio='none' aria-hidden='true'>
          <polyline className='bc_fd_chart_line' points={points} fill='none' strokeWidth='1.2' vectorEffect='non-scaling-stroke' />
        </svg>
        <div className='bc_fd_chart_groups'>
          {rows.map((row, index) => {
            const firstHeight = Math.max(2, (Math.abs(numberValue(row[firstKey])) / max) * chartHeight);
            const secondHeight = Math.max(2, (Math.abs(numberValue(row[secondKey])) / max) * chartHeight);
            return (
              <div className='bc_fd_chart_group' key={`${getRowLabel(row, index)}-${index}`}>
                <div className='bc_fd_chart_bars'>
                  <i className='is-teal' style={{ height: `${firstHeight}px` }} title={`${labels[0]}: ${formatMillion(row[firstKey], 2)} triệu`} />
                  <i className='is-gray' style={{ height: `${secondHeight}px` }} title={`${labels[1]}: ${formatMillion(row[secondKey], 2)} triệu`} />
                </div>
                <span>{getRowLabel(row, index)}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className='bc_fd_legend'>
        <span><i className='is-teal' />{labels[0]}</span>
        <span><i className='is-gray' />{labels[1]}</span>
        <span><i className='is-orange-line' />{labels[2]}</span>
      </div>
    </div>
  );
};

const RevenuePanel = ({ rows, summary, loading, periodLabel }) => {
  const profit = hasMetric(summary.profit) ? numberValue(summary.profit) : numberValue(summary.revenue) - numberValue(summary.expense);
  return (
    <section className='bc_fd_panel bc_fd_revenue_panel'>
      <PanelHeader title='Doanh thu, chi phí, lợi nhuận' unit='Triệu' dateLabel={periodLabel} />
      <div className='bc_fd_summary_triplet'>
        <div><strong>{loading ? '...' : formatMillion(summary.revenue)}</strong><span>DOANH THU THUẦN</span></div>
        <div><strong>{loading ? '...' : formatMillion(summary.expense)}</strong><span>CHI PHÍ</span></div>
        <div><strong>{loading ? '...' : formatSignedMillion(profit)}</strong><span>LỢI NHUẬN</span></div>
      </div>
      <MixedBarChart rows={rows} firstKey='revenue' secondKey='expense' lineKey='profit' labels={['DOANH THU THUẦN', 'CHI PHÍ', 'LỢI NHUẬN']} loading={loading} />
    </section>
  );
};

const TopProductsPanel = ({ rows = [], summary, loading, periodLabel }) => {
  const total = numberValue(summary.product_revenue) || rows.reduce((sum, row) => sum + numberValue(row.revenue), 0);
  return (
    <section className='bc_fd_panel bc_fd_products_panel'>
      <PanelHeader title='Mặt hàng theo đơn đã xuất kho' unit='Triệu' dateLabel={periodLabel} />
      <strong className='bc_fd_products_total'>{loading ? '...' : formatMillion(total)}</strong>
      <span className='bc_fd_metric_label'>GIÁ TRỊ ĐƠN ĐÃ XUẤT KHO (GỒM VAT)</span>
      <div className='bc_fd_products_table'>
        <div className='bc_fd_products_head'><span>Tên VTHH</span><span>Tỉ trọng</span><span>Doanh thu</span></div>
        {rows.map((row, index) => (
          <div className='bc_fd_product_row' key={`${row.product_id || row.product_name}-${index}`}>
            <span title={row.product_name}><i style={{ background: PRODUCT_COLORS[index % PRODUCT_COLORS.length] }} />{row.product_name || 'Khác'}</span>
            <span>{formatPercent(row.share_percent)}</span>
            <span>{formatMillion(row.revenue, 2)}</span>
          </div>
        ))}
        {!rows.length ? <EmptyState loading={loading} /> : null}
      </div>
    </section>
  );
};

const HealthPanel = ({ summary, loading, periodLabel }) => {
  const rows = [
    ['Lợi nhuận/Vốn chủ sở hữu (ROE)', formatOptionalPercent(summary.roe)],
    ['Tỷ suất sinh lời của tài sản (ROA)', formatOptionalPercent(summary.roa)],
    ['Tỷ suất sinh lời/Tổng vốn đầu tư (ROI)', formatOptionalPercent(summary.roi)],
  ];
  const structureRows = [
    ['Hệ số nợ', formatOptionalPercent(summary.debt_ratio)],
    ['Hệ số vốn chủ sở hữu', formatOptionalPercent(summary.equity_ratio)],
    ['Hệ số nợ trên vốn chủ sở hữu', hasMetric(summary.debt_to_equity) ? formatNumber(summary.debt_to_equity, 2) : '—'],
    ['Tỷ trọng tài sản dài hạn', formatOptionalPercent(summary.fixed_asset_ratio)],
  ];

  const renderRow = ([label, value]) => <div className='bc_fd_health_row' key={label}><span>{label}</span><b>{loading ? '...' : value}</b></div>;
  return (
    <section className='bc_fd_panel bc_fd_health_panel'>
      <PanelHeader title='Tình hình sức khỏe doanh nghiệp' dateLabel={periodLabel} />
      <div className='bc_fd_health_list'>
        {rows.map(renderRow)}
        <div className='bc_fd_health_group'><FiChevronDown /><strong>Cơ cấu tài chính và cơ cấu tài sản</strong></div>
        {structureRows.map(renderRow)}
      </div>
    </section>
  );
};

const ExpensePanel = ({ rows = [], loading }) => {
  const total = rows.reduce((sum, row) => sum + Math.abs(numberValue(row.amount)), 0);
  const max = Math.max(1, ...rows.map((row) => Math.abs(numberValue(row.amount))));
  return (
    <section className='bc_fd_panel bc_fd_expense_panel'>
      <PanelHeader title='Chi phí theo Khoản mục chi phí' />
      <strong className='bc_fd_expense_total'>{loading ? '...' : formatMillion(total)}</strong>
      <span className='bc_fd_metric_label'>TỔNG</span>
      <div className='bc_fd_horizontal_chart'>
        {rows.map((row, index) => (
          <div key={`${row.category_name}-${index}`} title={`${row.category_name}: ${formatMillion(row.amount, 2)} triệu`}>
            <span>{row.category_name}</span>
            <i><b style={{ width: `${Math.max(3, Math.abs(numberValue(row.amount)) / max * 100)}%` }} /></i>
            <em>{formatMillion(row.amount, 1)}</em>
          </div>
        ))}
        {!rows.length ? <EmptyState loading={loading} /> : null}
      </div>
    </section>
  );
};

const AgingPanel = ({ rows = [], loading, dateLabel }) => {
  const max = Math.max(1, ...rows.flatMap((row) => [row.receivable, row.payable]).map((value) => Math.abs(numberValue(value))));
  return (
    <section className='bc_fd_panel bc_fd_aging_panel'>
      <PanelHeader title='Phân tích công nợ theo tuổi nợ' unit='Triệu' dateLabel={dateLabel} />
      {rows.length ? (
        <div className='bc_fd_aging_chart'>
          <div className='bc_fd_aging_plot'>
            {rows.map((row, index) => (
              <div className='bc_fd_aging_group' key={`${row.bucket_key || row.bucket_label}-${index}`}>
                <div>
                  <i className='is-teal' style={{ height: `${Math.max(2, Math.abs(numberValue(row.receivable)) / max * 190)}px` }} title={`Phải thu: ${formatMillion(row.receivable, 2)} triệu`} />
                  <i className='is-orange' style={{ height: `${Math.max(2, Math.abs(numberValue(row.payable)) / max * 190)}px` }} title={`Phải trả: ${formatMillion(row.payable, 2)} triệu`} />
                </div>
                <span>{row.bucket_label}</span>
              </div>
            ))}
          </div>
          <div className='bc_fd_legend'><span><i className='is-teal' />PHẢI THU</span><span><i className='is-orange' />PHẢI TRẢ</span></div>
        </div>
      ) : <EmptyState loading={loading} />}
    </section>
  );
};

const CashFlowPanel = ({ rows = [], summary, loading }) => (
  <section className='bc_fd_panel bc_fd_cashflow_panel'>
    <PanelHeader title='Dòng tiền' unit='Triệu' />
    <div className='bc_fd_summary_triplet'>
      <div><strong>{loading ? '...' : formatMillion(summary.cash_in)}</strong><span>TỔNG THU</span></div>
      <div><strong className='is-negative'>{loading ? '...' : `(${formatMillion(summary.cash_out)})`}</strong><span>TỔNG CHI</span></div>
      <div><strong className={numberValue(summary.net_cash) < 0 ? 'is-negative' : ''}>{loading ? '...' : numberValue(summary.net_cash) < 0 ? `(${formatMillion(summary.net_cash)})` : formatMillion(summary.net_cash)}</strong><span>TỒN</span></div>
    </div>
    <MixedBarChart rows={rows} firstKey='cash_in' secondKey='cash_out' lineKey='net_cash' labels={['THU', 'CHI', 'TỒN']} loading={loading} />
  </section>
);

const MetricTile = ({ label, value, note, tone = 'neutral', loading }) => (
  <div className={`bc_fd_metric_tile is-${tone}`}>
    <span>{label}</span>
    <strong>{loading ? '...' : value}</strong>
    <small>{note}</small>
  </div>
);

const LiquidityPanel = ({ summary, loading }) => {
  const currentRatio = numberValue(summary.current_ratio);
  const quickRatio = numberValue(summary.quick_ratio);
  const workingCapital = numberValue(summary.working_capital);
  const ratioTone = !hasMetric(summary.current_ratio) ? 'neutral' : currentRatio < 1 ? 'danger' : currentRatio < 1.5 ? 'warning' : 'positive';
  const quickTone = !hasMetric(summary.quick_ratio) ? 'neutral' : quickRatio < 0.8 ? 'danger' : quickRatio < 1 ? 'warning' : 'positive';

  return (
    <section className='bc_fd_panel bc_fd_management_panel'>
      <PanelHeader title='Thanh khoản và vốn lưu động' />
      <p className='bc_fd_panel_intro'>Khả năng đáp ứng nghĩa vụ ngắn hạn tại ngày cuối kỳ.</p>
      <div className='bc_fd_metric_tiles'>
        <MetricTile label='Vốn lưu động ròng' value={formatSignedMillion(workingCapital, 1)} note='Triệu đồng' tone={workingCapital < 0 ? 'danger' : 'positive'} loading={loading} />
        <MetricTile label='Thanh toán hiện hành' value={formatRatio(summary.current_ratio)} note='Mốc theo dõi: ≥ 1,0' tone={ratioTone} loading={loading} />
        <MetricTile label='Thanh toán nhanh' value={formatRatio(summary.quick_ratio)} note='Không gồm hàng tồn kho' tone={quickTone} loading={loading} />
        <MetricTile label='Hệ số tiền mặt' value={formatRatio(summary.cash_ratio)} note='Tiền/Nợ ngắn hạn' tone={numberValue(summary.cash_ratio) < 0.2 ? 'warning' : 'neutral'} loading={loading} />
      </div>
    </section>
  );
};

const ProfitabilityPanel = ({ summary, loading }) => {
  const netMargin = numberValue(summary.net_margin);
  const grossMargin = numberValue(summary.gross_margin);
  return (
    <section className='bc_fd_panel bc_fd_management_panel'>
      <PanelHeader title='Hiệu quả sinh lời' />
      <p className='bc_fd_panel_intro'>Đo chất lượng doanh thu và mức chuyển hóa doanh thu thành lợi nhuận.</p>
      <div className='bc_fd_metric_tiles'>
        <MetricTile label='Lợi nhuận gộp' value={formatSignedMillion(summary.gross_profit, 1)} note='Triệu đồng' tone={numberValue(summary.gross_profit) < 0 ? 'danger' : 'positive'} loading={loading} />
        <MetricTile label='Biên lợi nhuận gộp' value={formatOptionalPercent(summary.gross_margin)} note='Sau giá vốn hàng bán' tone={grossMargin < 0 ? 'danger' : grossMargin < 15 ? 'warning' : 'positive'} loading={loading} />
        <MetricTile label='Biên lợi nhuận ròng' value={formatOptionalPercent(summary.net_margin)} note='Lợi nhuận/Doanh thu' tone={netMargin < 0 ? 'danger' : netMargin < 5 ? 'warning' : 'positive'} loading={loading} />
        <MetricTile label='Vòng quay tài sản' value={formatRatio(summary.asset_turnover)} note='Doanh thu/Tổng tài sản' tone='neutral' loading={loading} />
      </div>
    </section>
  );
};

const WorkingCapitalCyclePanel = ({ summary, loading }) => (
  <section className='bc_fd_panel bc_fd_management_panel'>
    <PanelHeader title='Chu kỳ vốn lưu động' />
    <p className='bc_fd_panel_intro'>Ước tính từ số dư cuối kỳ và doanh thu/giá vốn trong kỳ.</p>
    <div className='bc_fd_cycle_flow'>
      <div><span>Thu tiền</span><strong>{loading ? '...' : formatDays(summary.receivable_days)}</strong><small>DSO</small></div>
      <i>+</i>
      <div><span>Lưu kho</span><strong>{loading ? '...' : formatDays(summary.inventory_days)}</strong><small>DIO</small></div>
      <i>−</i>
      <div><span>Trả nhà cung cấp</span><strong>{loading ? '...' : formatDays(summary.payable_days)}</strong><small>DPO</small></div>
    </div>
    <div className={`bc_fd_cycle_result${numberValue(summary.cash_conversion_cycle) > 60 ? ' is-warning' : ''}`}>
      <span>Chu kỳ chuyển đổi tiền</span>
      <strong>{loading ? '...' : formatDays(summary.cash_conversion_cycle)}</strong>
    </div>
    <div className='bc_fd_cycle_note'>Tiền hiện có đủ trang trải khoảng <b>{loading ? '...' : formatDays(summary.cash_coverage_days)}</b> chi phí theo tốc độ hiện tại.</div>
  </section>
);

const FinancialAlertsPanel = ({ summary, loading }) => {
  const receivable = numberValue(summary.receivable);
  const overdueRatio = receivable ? numberValue(summary.receivable_overdue) * 100 / receivable : 0;
  const alerts = [];

  if (hasMetric(summary.balance_data_complete) && numberValue(summary.balance_data_complete) === 0) alerts.push({ tone: 'danger', title: 'Bảng cân đối chưa cân', text: `Tài sản đang lệch ${formatSignedMoney(summary.balance_difference)} đồng so với Nợ phải trả + Vốn chủ sở hữu. Cần đối chiếu số dư đầu kỳ và chứng từ đã ghi sổ trước khi dùng các tỷ lệ quản trị.` });
  if (hasMetric(summary.liability_maturity_data_available) && numberValue(summary.liability_maturity_data_available) === 0) alerts.push({ tone: 'warning', title: 'Chưa khai báo kỳ hạn nợ phải trả', text: 'Chưa đủ dữ liệu đáo hạn để tách nợ ngắn hạn và dài hạn. Các tỷ lệ thanh khoản đang dùng toàn bộ nợ phải trả như nợ ngắn hạn để tránh đánh giá lạc quan sai.' });
  if (hasMetric(summary.current_ratio) && numberValue(summary.current_ratio) < 1) alerts.push({ tone: 'danger', title: 'Thiếu hụt thanh khoản ngắn hạn', text: 'Tài sản ngắn hạn chưa đủ bù nợ ngắn hạn; cần ưu tiên kế hoạch thu tiền và cơ cấu lịch trả nợ.' });
  if (hasMetric(summary.net_margin) && numberValue(summary.net_margin) < 0) alerts.push({ tone: 'danger', title: 'Hoạt động trong kỳ đang lỗ', text: `Biên lợi nhuận ròng ${formatOptionalPercent(summary.net_margin)}; cần rà soát giá vốn và nhóm chi phí lớn.` });
  if (!numberValue(summary.revenue) && numberValue(summary.expense) > 0) alerts.push({ tone: 'danger', title: 'Có chi phí nhưng chưa ghi nhận doanh thu', text: 'Cần kiểm tra thời điểm ghi nhận doanh thu, kết chuyển giá vốn và phạm vi dữ liệu đang chọn.' });
  if (overdueRatio >= 20) alerts.push({ tone: 'warning', title: 'Tỷ lệ phải thu quá hạn cao', text: `${formatNumber(overdueRatio, 1)}% công nợ phải thu đang quá 30 ngày.` });
  if (hasMetric(summary.receivable_days) && numberValue(summary.receivable_days) > 120) alerts.push({ tone: 'warning', title: 'Vòng quay phải thu chậm', text: `Kỳ thu tiền ước tính ${formatDays(summary.receivable_days)}; cần đối chiếu hạn nợ và kế hoạch thu hồi.` });
  if (hasMetric(summary.cash_coverage_days) && numberValue(summary.cash_coverage_days) < 30) alerts.push({ tone: 'warning', title: 'Dự trữ tiền mặt mỏng', text: `Tiền hiện có chỉ tương đương ${formatDays(summary.cash_coverage_days)} chi phí.` });
  if (hasMetric(summary.debt_ratio) && numberValue(summary.debt_ratio) > 70) alerts.push({ tone: 'warning', title: 'Đòn bẩy tài chính cao', text: `Nợ đang tương đương ${formatOptionalPercent(summary.debt_ratio)} tổng tài sản.` });
  if (!numberValue(summary.equity)) alerts.push({ tone: 'warning', title: 'Chưa có số dư vốn chủ sở hữu', text: 'ROE và hệ số nợ/vốn chủ sở hữu chưa thể đánh giá; cần kiểm tra số dư tài khoản 411.' });

  if (!alerts.length) alerts.push({ tone: 'positive', title: 'Chưa phát hiện ngưỡng cảnh báo trọng yếu', text: 'Các ngưỡng thanh khoản, lợi nhuận, công nợ và dự trữ tiền mặt đang trong vùng theo dõi bình thường.' });

  return (
    <section className='bc_fd_panel bc_fd_alerts_panel'>
      <PanelHeader title='Cảnh báo quản trị tài chính' compact />
      <p className='bc_fd_panel_intro'>Cảnh báo tự động theo dữ liệu kỳ hiện tại, không thay thế đánh giá chuyên môn.</p>
      <div className='bc_fd_alert_list'>
        {loading ? <EmptyState loading /> : alerts.slice(0, 4).map((alert) => (
          <div className={`is-${alert.tone}`} key={alert.title}>
            {alert.tone === 'positive' ? <FiCheckCircle /> : <FiAlertCircle />}
            <p><strong>{alert.title}</strong><span>{alert.text}</span></p>
          </div>
        ))}
      </div>
    </section>
  );
};

const csvCell = (value) => `"${`${value ?? ''}`.replace(/"/g, '""')}"`;

const FinancialDashboardPage = ({ mode = 'overview' }) => {
  const history = useHistory();
  const analysis = mode === 'analysis';
  const [scope, setScope] = useState(getPortalScope);
  const [range, setRange] = useState(DEFAULT_RANGE);
  const request = useCallback((signal) => {
    const params = { ...range, ...scope };
    return analysis ? getFinancialAnalysis(params, { signal }) : getFinancialOverview(params, { signal });
  }, [analysis, range, scope]);
  const { data, loading, error, load } = useDashboardReport(request, EMPTY_DATA);
  useEffect(() => {
    const handler = (event) => setScope(event?.detail || getPortalScope());
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, handler);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, handler);
  }, []);

  const summary = data.summary || {};
  const periodLabel = useMemo(() => {
    const from = moment(range.date_from, 'YYYY-MM-DD');
    const to = moment(range.date_to, 'YYYY-MM-DD');
    if (!from.isValid() || !to.isValid()) return 'Kỳ báo cáo';
    if (from.isSame(to, 'year') && from.date() === 1 && from.month() === 0) return 'Đầu năm đến hiện tại';
    return `${from.format('DD/MM/YYYY')} - ${to.format('DD/MM/YYYY')}`;
  }, [range]);
  const asOfLabel = moment(range.date_to, 'YYYY-MM-DD').isValid() ? moment(range.date_to).format('DD/MM/YYYY') : '';

  const applyPreset = (value) => {
    const dateTo = moment();
    const dateFrom = value === 'month' ? moment().startOf('month') : value === 'quarter' ? moment().startOf('quarter') : moment().startOf('year');
    setRange({ date_from: dateFrom.format('YYYY-MM-DD'), date_to: dateTo.format('YYYY-MM-DD') });
  };

  const exportCsv = () => {
    const rows = analysis
      ? [
          ['Chỉ số', 'Giá trị'],
          ['ROE (%)', summary.roe], ['ROA (%)', summary.roa], ['ROI (%)', summary.roi],
          ['Vốn lưu động ròng', summary.working_capital], ['Hệ số thanh toán hiện hành', summary.current_ratio],
          ['Hệ số thanh toán nhanh', summary.quick_ratio], ['Hệ số tiền mặt', summary.cash_ratio],
          ['Lợi nhuận gộp', summary.gross_profit], ['Biên lợi nhuận gộp (%)', summary.gross_margin],
          ['Biên lợi nhuận ròng (%)', summary.net_margin], ['Vòng quay tài sản', summary.asset_turnover],
          ['Kỳ thu tiền (ngày)', summary.receivable_days], ['Kỳ lưu kho (ngày)', summary.inventory_days],
          ['Kỳ trả nhà cung cấp (ngày)', summary.payable_days], ['Chu kỳ chuyển đổi tiền (ngày)', summary.cash_conversion_cycle],
          ['Số ngày chi phí được đảm bảo bằng tiền', summary.cash_coverage_days],
          [], ['Tuổi nợ', 'Phải thu', 'Phải trả'],
          ...(data.debt_aging || []).map((row) => [row.bucket_label, row.receivable, row.payable]),
          [], ['Kỳ', 'Thu', 'Chi', 'Tồn'],
          ...(data.cash_flow || []).map((row, index) => [getRowLabel(row, index), row.cash_in, row.cash_out, row.net_cash]),
        ]
      : [
          ['Chỉ số', 'Giá trị'],
          ['Tổng tiền', numberValue(summary.cash_balance) + numberValue(summary.bank_balance)],
          ['Phải thu', summary.receivable], ['Phải trả', summary.payable], ['Doanh thu', summary.revenue],
          ['Chi phí', summary.expense], ['Lợi nhuận', summary.profit], ['Hàng tồn kho', summary.inventory],
          [], ['Kỳ', 'Doanh thu', 'Chi phí', 'Lợi nhuận'],
          ...(data.trend || []).map((row, index) => [getRowLabel(row, index), row.revenue, row.expense, row.profit]),
          [], ['Mặt hàng', 'Tỉ trọng (%)', 'Doanh thu'],
          ...(data.top_products || []).map((row) => [row.product_name, row.share_percent, row.revenue]),
        ];
    const content = `\ufeff${rows.map((row) => row.map(csvCell).join(',')).join('\n')}`;
    const href = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = `${analysis ? 'phan-tich' : 'tong-quan'}-tai-chinh-${range.date_to}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 0);
  };

  return (
    <div className='bw_main_wrapp bc_fd_page'>
      <nav className='bc_fd_tabs' aria-label='Báo cáo tài chính'>
        {REPORT_TABS.map((tab) => (
          <button type='button' className={tab.key === mode ? 'is-active' : ''} onClick={() => history.push(tab.path)} key={tab.key}>
            {tab.label}
          </button>
        ))}
      </nav>

      <header className='bc_fd_header'>
        <div>
          <h2>{analysis ? 'Phân tích tài chính' : 'Biểu đồ tài chính'}</h2>
          <p>{scope.store_name || scope.business_name || scope.company_name || 'Toàn hệ thống'}</p>
        </div>
        <div className='bc_fd_actions'>
          <button type='button' disabled={loading || Boolean(error)} onClick={() => window.print()}><FiPrinter /> In</button>
          <button type='button' disabled={loading || Boolean(error)} onClick={exportCsv}><FiDownload /> Xuất khẩu</button>
        </div>
      </header>

      <div className='bc_fd_filterbar'>
        <div className='bc_fd_presets'>
          <button type='button' onClick={() => applyPreset('year')}>Đầu năm đến hiện tại</button>
          <button type='button' onClick={() => applyPreset('quarter')}>Quý này</button>
          <button type='button' onClick={() => applyPreset('month')}>Tháng này</button>
        </div>
        <div className='bc_fd_filters'>
          <FiCalendar />
          <input aria-label='Từ ngày' type='date' value={range.date_from} max={range.date_to} onChange={(event) => setRange((current) => ({ ...current, date_from: event.target.value }))} />
          <span>đến</span>
          <input aria-label='Đến ngày' type='date' value={range.date_to} min={range.date_from} onChange={(event) => setRange((current) => ({ ...current, date_to: event.target.value }))} />
          <button type='button' className='bc_fd_refresh' title='Tải lại' onClick={load}><FiRefreshCw /></button>
        </div>
      </div>

      {error ? <div className='bc_fd_error'>{error}<button type='button' onClick={load}>Thử lại</button></div> : null}

      {error ? null : !analysis ? (
        <main className='bc_fd_dashboard_grid'>
          <div className='bc_fd_col_6'><FinancialSnapshot summary={summary} loading={loading} periodLabel={periodLabel} /></div>
          <div className='bc_fd_col_3'><StatusCard title='Phải thu' total={summary.receivable} current={summary.receivable_current} overdue={summary.receivable_overdue} unclassified={summary.receivable_unclassified} dueDataAvailable={numberValue(summary.debt_due_data_available) === 1} loading={loading} dateLabel={asOfLabel} /></div>
          <div className='bc_fd_col_3'><StatusCard title='Phải trả' total={summary.payable} current={summary.payable_current} overdue={summary.payable_overdue} unclassified={summary.payable_unclassified} dueDataAvailable={numberValue(summary.debt_due_data_available) === 1} loading={loading} dateLabel={asOfLabel} /></div>
          <div className='bc_fd_col_6'><RevenuePanel rows={data.trend} summary={summary} loading={loading} periodLabel={periodLabel} /></div>
          <div className='bc_fd_col_6'><TopProductsPanel rows={data.top_products} summary={summary} loading={loading} periodLabel={periodLabel} /></div>
        </main>
      ) : (
        <main className='bc_fd_dashboard_grid'>
          <div className='bc_fd_col_6'><HealthPanel summary={summary} loading={loading} periodLabel={periodLabel} /></div>
          <div className='bc_fd_col_6'><ExpensePanel rows={data.expense_categories} loading={loading} /></div>
          <div className='bc_fd_col_6'><LiquidityPanel summary={summary} loading={loading} /></div>
          <div className='bc_fd_col_6'><ProfitabilityPanel summary={summary} loading={loading} /></div>
          <div className='bc_fd_col_6'><AgingPanel rows={data.debt_aging} loading={loading} dateLabel={asOfLabel} /></div>
          <div className='bc_fd_col_6'><CashFlowPanel rows={data.cash_flow} summary={summary} loading={loading} /></div>
          <div className='bc_fd_col_6'><WorkingCapitalCyclePanel summary={summary} loading={loading} /></div>
          <div className='bc_fd_col_6'><FinancialAlertsPanel summary={summary} loading={loading} /></div>
        </main>
      )}
    </div>
  );
};

export default FinancialDashboardPage;
